import WarningAmber from "@mui/icons-material/WarningAmber";
import {
	Box,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import React, { useCallback } from "react";

import { colors } from "../../../../../../config/colors";
import type {
	PastTimeOffAccess,
	PastTimeOffRole,
} from "../controllers/use-past-time-off-roles";
import { MAX_LOOKBACK_DAYS } from "../controllers/use-past-time-off-roles";

const filledInputStyles = {
	"& .MuiInputBase-input": {
		background: "#FFF",
		padding: "8px",
	},
};

// Days to pre-fill the first time a role is limited.
const DEFAULT_LOOKBACK_DAYS = "30";

interface RoleAccessRowProps {
	role: PastTimeOffRole;
	isDaysInvalid: boolean;
	isLast: boolean;
	onChange: (roleId: number, changes: Partial<PastTimeOffRole>) => void;
}

const RoleAccessRow: React.FC<RoleAccessRowProps> = ({
	role,
	isDaysInvalid,
	isLast,
	onChange,
}) => {
	const handleAccessChange = useCallback(
		(event: SelectChangeEvent<PastTimeOffAccess>) => {
			const access = event.target.value as PastTimeOffAccess;
			onChange(role.roleId, {
				access,
				days:
					access === "limited" && role.days === ""
						? DEFAULT_LOOKBACK_DAYS
						: role.days,
			});
		},
		[onChange, role.roleId, role.days]
	);

	const handleDaysChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const days = event.target.value.replace(/\D/g, "").slice(0, 5);
			onChange(role.roleId, { days });
		},
		[onChange, role.roleId]
	);

	const selectId = `past-time-off-access-${role.roleId}`;

	return (
		<Box
			sx={{
				paddingTop: "12px",
				paddingBottom: "12px",
				borderBottom: isLast ? "none" : "1px solid rgba(0,0,0,0.12)",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "16px",
					flexWrap: "wrap",
				}}
			>
				<Typography
					id={`${selectId}-label`}
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "16px",
						color: "rgba(0, 0, 0, 0.87)",
					}}
				>
					{role.label}
				</Typography>
				<Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
					{role.access === "limited" ? (
						<>
							<TextField
								variant='filled'
								size='small'
								value={role.days}
								onChange={handleDaysChange}
								error={isDaysInvalid}
								inputProps={{
									inputMode: "numeric",
									"aria-label": `Days back for ${role.label}`,
								}}
								sx={{ ...filledInputStyles, width: "80px" }}
							/>
							<Typography
								sx={{
									fontFamily: "Roboto",
									fontSize: "14px",
									color: "rgba(0, 0, 0, 0.6)",
									marginRight: "8px",
								}}
							>
								days back
							</Typography>
						</>
					) : null}
					<Select
						id={selectId}
						variant='filled'
						size='small'
						value={role.access}
						onChange={handleAccessChange}
						inputProps={{ "aria-labelledby": `${selectId}-label` }}
						sx={{ ...filledInputStyles, width: "160px" }}
					>
						<MenuItem value='none'>Not allowed</MenuItem>
						<MenuItem value='limited'>Limited</MenuItem>
						<MenuItem value='unlimited'>No limit</MenuItem>
					</Select>
				</Box>
			</Box>
			{isDaysInvalid ? (
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "flex-end",
						gap: "4px",
						marginTop: "4px",
					}}
				>
					<WarningAmber sx={{ color: colors.error, fontSize: "14px" }} />
					<Typography
						sx={{
							fontFamily: "Roboto",
							fontWeight: 400,
							fontSize: "12px",
							color: colors.error,
						}}
					>
						Enter 1 to {MAX_LOOKBACK_DAYS.toLocaleString()} days to save.
					</Typography>
				</Box>
			) : null}
		</Box>
	);
};

export default React.memo(RoleAccessRow);
